/**
 * Import Progress Indicator — the batch ribbon.
 *
 * With several sources in flight («Importar todo»), one segment per import,
 * coloured by its state, tells at a glance how far the batch is; the line
 * under it says it in words. Rendered only from two imports up: for one, its
 * own row already is the summary.
 */

import { useTranslation } from 'react-i18next'

import { STATE_FILL_CLASS, type ImportRowState } from './ImportRowParts'

/** Ribbon order: what is settled first, then what is moving, then what waits. */
const ORDER: ImportRowState[] = ['done', 'failed', 'running', 'waiting', 'queued']

interface ImportBatchSummaryProps {
  /** One state per tracked import (live, finished or failed this session). */
  states: ImportRowState[]
}

export const ImportBatchSummary = ({ states }: ImportBatchSummaryProps) => {
  const { t } = useTranslation('imports')
  if (states.length < 2) return null

  const sorted = [...states].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b))
  const done = states.filter((s) => s === 'done').length
  const failed = states.filter((s) => s === 'failed').length
  const finished = done + failed

  return (
    <div className="px-3 pt-3 space-y-1.5" data-testid="import-batch-summary">
      <div className="flex h-1.5 gap-0.5" aria-hidden="true">
        {sorted.map((state, index) => (
          <span
            key={`${state}-${index}`}
            className={`flex-1 rounded-full ${STATE_FILL_CLASS[state]} ${
              state === 'running' ? 'motion-safe:animate-pulse' : ''
            }`}
          />
        ))}
      </div>
      <p className="flex items-center justify-between gap-2 text-xs tabular-nums text-crypto-muted">
        <span>
          {t('progressIndicator.batch.finished', {
            finished,
            total: states.length,
            defaultValue: '{{finished}} of {{total}} finished',
          })}
        </span>
        {failed > 0 && (
          <span className="text-crypto-danger">
            {t('progressIndicator.batch.failed', {
              count: failed,
              defaultValue: '{{count}} failed',
            })}
          </span>
        )}
      </p>
    </div>
  )
}
